import type { ConflictResult, ConflictRule, ConflictRuleContext } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import { buildEffectiveSkillRatings, findUnmetSkillRequirements } from '../../utils/skill-matching.utils';

/**
 * Detects conflicts where a person is assigned to a shift whose required skills
 * they do not satisfy — either the skill is absent entirely (`skill_missing`) or
 * it is held below the shift's `minRating` threshold
 * (`skill_rating_below_minimum`).
 *
 * This is the conflict-layer counterpart to the hard eligibility filters used by
 * the solver pre-filter, grid drag & drop, swaps and open-shift applications.
 * Those block an assignment as it is made; this rule catches assignments that
 * arrived by a path with no such gate — the plan-instance command layer
 * (`planning-commands.service`, used by the AI `planning.assignEmployee` tool),
 * imported data, or a shift whose requirements were tightened after the fact.
 *
 * Deliberately distinct from {@link SkillExpiredConflictRule}: a held-but-lapsed
 * certification is a different remediation (renew) from an absent skill (assign
 * someone else) or an under-rated one (train, or lower the threshold).
 *
 * Requires `context.personSkills` and `context.shiftSkillRequirements`. Returns
 * an empty result set when either is missing, so callers that do not load skill
 * data are unaffected.
 */
export class SkillQualificationConflictRule implements ConflictRule {
  /** Reported per-result; a single rule emits both skill conflict types. */
  readonly type = 'skill_missing' as const;

  detect(schedule: ScheduleMap, _events: unknown[], context?: ConflictRuleContext): ConflictResult[] {
    const personSkills = context?.personSkills;
    const shiftSkillRequirements = context?.shiftSkillRequirements;
    if (!personSkills || !shiftSkillRequirements) return [];

    const skillNames = context?.skillNames;
    const results: ConflictResult[] = [];
    // One ratings map per person, not per (person, date, unit) — a person's
    // effective ratings do not vary across the schedule.
    const ratingsByPerson = new Map<string, ReturnType<typeof buildEffectiveSkillRatings>>();

    /** Effective ratings per person, memoised — they do not vary across the schedule. */
    const ratingsFor = (personId: string) => {
      let ratings = ratingsByPerson.get(personId);
      if (!ratings) {
        // Expiry is deliberately NOT filtered here — that is
        // SkillExpiredConflictRule's job, and filtering would double-report a
        // lapsed skill as missing.
        ratings = buildEffectiveSkillRatings(personSkills.get(personId) ?? []);
        ratingsByPerson.set(personId, ratings);
      }
      return ratings;
    };

    for (const [date, daySchedule] of Object.entries(schedule)) {
      for (const [personId, unitIds] of Object.entries(daySchedule)) {
        for (const unitId of unitIds) {
          const conflict = this.checkUnit(date, personId, unitId, ratingsFor, shiftSkillRequirements, skillNames);
          if (conflict) results.push(conflict);
        }
      }
    }

    return results;
  }

  /** Evaluate one (person, date, unit) triple. Returns null when fully qualified. */
  private checkUnit(
    date: string,
    personId: string,
    unitId: string,
    ratingsFor: (personId: string) => ReturnType<typeof buildEffectiveSkillRatings>,
    shiftSkillRequirements: NonNullable<ConflictRuleContext['shiftSkillRequirements']>,
    skillNames: ConflictRuleContext['skillNames']
  ): ConflictResult | null {
    const requirements = shiftSkillRequirements.get(unitId);
    if (!requirements || requirements.length === 0) return null;

    const unmet = findUnmetSkillRequirements(ratingsFor(personId), requirements);
    if (unmet.length === 0) return null;

    // Missing outright outranks a rating shortfall: it is the blunter problem
    // and the one a planner must act on first.
    const missing = unmet.find(u => !u.held);
    const reported = missing ?? unmet[0];

    return {
      type: missing ? 'skill_missing' : 'skill_rating_below_minimum',
      date,
      personId,
      eventLabel: skillNames?.get(reported.skillId) ?? reported.skillId,
      calendarId: '',
      shiftUnitId: unitId,
    };
  }
}
